import { useEffect, useMemo, useRef, useState } from "react";

import {
  Message,
  ToolContentBlock,
} from "../streaming/streaming-message-types";

import { ConversationEngine, ConversationEngineState } from "./types";

export type UseConversationEngineOptions = {
  /**
   * When true, the hook filters messages to only those with `revealed !== false`.
   * Defaults to true so UIs only render staged/visible messages.
   */
  visibleOnly?: boolean;
};

export function useConversationEngine(
  engine: ConversationEngine | null | undefined,
  options: UseConversationEngineOptions = {}
) {
  const { visibleOnly = true } = options;

  const [messages, setMessages] = useState<Message[]>(() =>
    engine ? engine.getMessages() : []
  );
  const [state, setState] = useState<ConversationEngineState>(
    () => engine?.getState() ?? { kind: "awaiting_user_input", isBusy: false }
  );

  const engineRef = useRef<ConversationEngine | null>(null);
  engineRef.current = engine ?? null;

  const api = useMemo(
    () => ({
      sendUserMessage: async (text: string) => {
        if (!engineRef.current) return;
        await engineRef.current.sendUserMessage(text);
      },
      continueWithToolResults: async (content: ToolContentBlock[]) => {
        if (!engineRef.current) return;
        await engineRef.current.continueWithToolResults(content);
      },
      abort: () => engineRef.current?.abort(),
      reset: () => engineRef.current?.reset(),
    }),
    []
  );

  useEffect(() => {
    if (!engine) return;

    const handleMessages = ({ messages: next }: { messages: Message[] }) => {
      setMessages(next);
    };
    const handleState = ({
      state: next,
    }: {
      state: ConversationEngineState;
    }) => {
      setState(next);
    };

    setMessages(engine.getMessages());
    setState(engine.getState());
    engine.on("messagesUpdated", handleMessages);
    engine.on("stateChanged", handleState);

    return () => {
      engine.off("messagesUpdated", handleMessages);
      engine.off("stateChanged", handleState);
    };
  }, [engine]);

  const visibleMessages = useMemo(() => {
    if (!visibleOnly) return messages;
    return messages.filter((m) => m.displayState.kind !== "hidden");
  }, [messages, visibleOnly]);

  return {
    messages,
    visibleMessages,
    state,
    ...api,
  } as const;
}

// a hook that triggers re-renders only when the state of the chat changes,
// not the messages or other things. useful for components that only care about
// discrete updates. Use this instead of the full one to avoid constant re-renders
// when messages stream.
export function useConversationEngineStateOnly(
  engine: ConversationEngine | null | undefined
) {
  const [state, setState] = useState<ConversationEngineState>(
    () => engine?.getState() ?? { kind: "awaiting_user_input", isBusy: false }
  );

  const engineRef = useRef<ConversationEngine | null>(null);
  engineRef.current = engine ?? null;

  useEffect(() => {
    if (!engine) return;

    const handleState = ({
      state: next,
    }: {
      state: ConversationEngineState;
    }) => {
      setState(next);
    };

    setState(engine.getState());
    engine.on("stateChanged", handleState);

    return () => {
      engine.off("stateChanged", handleState);
    };
  }, [engine]);

  return {
    state,
  } as const;
}
